import React, {useCallback, useState} from 'react';
import {View, ScrollView, StyleSheet, Platform, TouchableOpacity} from 'react-native';
import {
  Text,
  TextInput,
  Button,
  Snackbar,
  Menu,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import GradientHeader from '../../components/common/GradientHeader';
import DateTimePicker from '@react-native-community/datetimepicker';
import {useOrderStore} from '../../store/useOrderStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {colors, spacing} from '../../theme';

const ORDER_STATUSES = [
  'NEW',
  'IN_PROCESS',
  'COMPLETED',
  'QUALITY_CHECK',
  'READY_FOR_TRIAL',
  'DELIVERED',
  'CANCELLED',
];

const OrderSearchFiltersScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {searchOrders} = useOrderStore();
  const {selectedOrgUnitId} = useOrgUnitStore();

  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState(null);
  const [orderDateFrom, setOrderDateFrom] = useState(null);
  const [orderDateTo, setOrderDateTo] = useState(null);
  const [deliveryDateFrom, setDeliveryDateFrom] = useState(null);
  const [deliveryDateTo, setDeliveryDateTo] = useState(null);

  const [showOrderDateFromPicker, setShowOrderDateFromPicker] = useState(false);
  const [showOrderDateToPicker, setShowOrderDateToPicker] = useState(false);
  const [showDeliveryDateFromPicker, setShowDeliveryDateFromPicker] = useState(false);
  const [showDeliveryDateToPicker, setShowDeliveryDateToPicker] = useState(false);
  const [statusMenuVisible, setStatusMenuVisible] = useState(false);

  const [searching, setSearching] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const formatDate = d =>
    d
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      : '';

  const handleReset = () => {
    setPhone('');
    setStatus(null);
    setOrderDateFrom(null);
    setOrderDateTo(null);
    setDeliveryDateFrom(null);
    setDeliveryDateTo(null);
  };

  const handleSearch = useCallback(async () => {
    setSearching(true);
    try {
      const payload = {};
      if (phone.trim()) payload.phone = phone.trim();
      if (status) payload.status = status;
      if (orderDateFrom) payload.orderDateFrom = formatDate(orderDateFrom);
      if (orderDateTo) payload.orderDateTo = formatDate(orderDateTo);
      if (deliveryDateFrom) payload.deliveryDateFrom = formatDate(deliveryDateFrom);
      if (deliveryDateTo) payload.deliveryDateTo = formatDate(deliveryDateTo);
      if (selectedOrgUnitId) payload.orgUnitId = selectedOrgUnitId;

      const results = await searchOrders(payload);
      navigation.navigate('OrderSearchResults', {results});
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    } finally {
      setSearching(false);
    }
  }, [phone, status, orderDateFrom, orderDateTo, deliveryDateFrom, deliveryDateTo, selectedOrgUnitId, searchOrders, navigation, t]);

  return (
    <View style={styles.container}>
      <GradientHeader title={t('order.searchOrders')} onBack={() => navigation.goBack()} />

      <ScrollView style={styles.form} keyboardShouldPersistTaps="handled">
        {/* Mobile Number */}
        <TextInput
          label={t('customer.phone')}
          value={phone}
          onChangeText={setPhone}
          mode="outlined"
          keyboardType="phone-pad"
          style={styles.input}
        />

        {/* Order Status */}
        <Menu
          visible={statusMenuVisible}
          onDismiss={() => setStatusMenuVisible(false)}
          anchor={
            <TextInput
              label={t('order.orderStatus')}
              value={status ? t(`order.status.${status}`) : ''}
              mode="outlined"
              style={styles.input}
              editable={false}
              right={<TextInput.Icon icon="chevron-down" />}
              onPressIn={() => setStatusMenuVisible(true)}
            />
          }>
          <Menu.Item
            title={t('expense.duration.all')}
            onPress={() => { setStatus(null); setStatusMenuVisible(false); }}
          />
          {ORDER_STATUSES.map(s => (
            <Menu.Item
              key={s}
              title={t(`order.status.${s}`)}
              onPress={() => { setStatus(s); setStatusMenuVisible(false); }}
            />
          ))}
        </Menu>

        {/* Order Date Range */}
        <Text style={styles.sectionLabel}>{t('order.orderDateRange')}</Text>
        <View style={styles.dateRow}>
          <View style={styles.dateField}>
            <TouchableOpacity activeOpacity={1} onPress={() => setShowOrderDateFromPicker(true)}>
              <TextInput
                label={t('report.dateFrom')}
                value={formatDate(orderDateFrom)}
                mode="outlined"
                style={styles.dateInput}
                editable={false}
                right={<TextInput.Icon icon="calendar" onPress={() => setShowOrderDateFromPicker(true)} />}
              />
            </TouchableOpacity>
          </View>
          <View style={styles.dateField}>
            <TouchableOpacity activeOpacity={1} onPress={() => setShowOrderDateToPicker(true)}>
              <TextInput
                label={t('report.dateTo')}
                value={formatDate(orderDateTo)}
                mode="outlined"
                style={styles.dateInput}
                editable={false}
                right={<TextInput.Icon icon="calendar" onPress={() => setShowOrderDateToPicker(true)} />}
              />
            </TouchableOpacity>
          </View>
        </View>
        {showOrderDateFromPicker && (
          <DateTimePicker
            value={orderDateFrom || new Date()}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, selectedDate) => {
              setShowOrderDateFromPicker(false);
              if (selectedDate) setOrderDateFrom(selectedDate);
            }}
          />
        )}
        {showOrderDateToPicker && (
          <DateTimePicker
            value={orderDateTo || new Date()}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, selectedDate) => {
              setShowOrderDateToPicker(false);
              if (selectedDate) setOrderDateTo(selectedDate);
            }}
          />
        )}

        {/* Delivery Date Range */}
        <Text style={styles.sectionLabel}>{t('order.deliveryDateRange')}</Text>
        <View style={styles.dateRow}>
          <View style={styles.dateField}>
            <TouchableOpacity activeOpacity={1} onPress={() => setShowDeliveryDateFromPicker(true)}>
              <TextInput
                label={t('report.dateFrom')}
                value={formatDate(deliveryDateFrom)}
                mode="outlined"
                style={styles.dateInput}
                editable={false}
                right={<TextInput.Icon icon="calendar" onPress={() => setShowDeliveryDateFromPicker(true)} />}
              />
            </TouchableOpacity>
          </View>
          <View style={styles.dateField}>
            <TouchableOpacity activeOpacity={1} onPress={() => setShowDeliveryDateToPicker(true)}>
              <TextInput
                label={t('report.dateTo')}
                value={formatDate(deliveryDateTo)}
                mode="outlined"
                style={styles.dateInput}
                editable={false}
                right={<TextInput.Icon icon="calendar" onPress={() => setShowDeliveryDateToPicker(true)} />}
              />
            </TouchableOpacity>
          </View>
        </View>
        {showDeliveryDateFromPicker && (
          <DateTimePicker
            value={deliveryDateFrom || new Date()}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, selectedDate) => {
              setShowDeliveryDateFromPicker(false);
              if (selectedDate) setDeliveryDateFrom(selectedDate);
            }}
          />
        )}
        {showDeliveryDateToPicker && (
          <DateTimePicker
            value={deliveryDateTo || new Date()}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, selectedDate) => {
              setShowDeliveryDateToPicker(false);
              if (selectedDate) setDeliveryDateTo(selectedDate);
            }}
          />
        )}

        <Button
          mode="text"
          onPress={handleReset}
          style={styles.resetButton}
          textColor={colors.primary}>
          {t('order.reset')}
        </Button>

        <Button
          mode="contained"
          onPress={handleSearch}
          loading={searching}
          disabled={searching}
          style={styles.searchButton}
          contentStyle={styles.searchButtonContent}>
          {t('common.search')}
        </Button>
      </ScrollView>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  form: {flex: 1, padding: spacing.md},
  input: {
    marginBottom: spacing.md,
    backgroundColor: colors.surface,
  },
  sectionLabel: {
    fontSize: 13,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
    marginTop: spacing.xs,
  },
  dateRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  dateField: {
    flex: 1,
  },
  dateInput: {
    backgroundColor: colors.surface,
  },
  resetButton: {
    alignSelf: 'center',
    marginTop: spacing.xs,
  },
  searchButton: {
    marginTop: spacing.md,
    marginBottom: spacing.xxl,
    backgroundColor: colors.primary,
  },
  searchButtonContent: {
    paddingVertical: spacing.xs,
  },
});

export default OrderSearchFiltersScreen;
